import React from "react";
import { Clock, User } from "lucide-react";

const STAGE_META = {
  new_lead: { label: "New Lead", dot: "bg-blue-500" },
  contacted: { label: "Contacted", dot: "bg-indigo-500" },
  consultation_booked: { label: "Consultation Booked", dot: "bg-violet-500" },
  proposal_sent: { label: "Proposal Sent", dot: "bg-amber-500" },
  won: { label: "Won / Active Client", dot: "bg-emerald-500" },
  lost: { label: "Lost / Not a Fit", dot: "bg-slate-400" },
};

function formatDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatDuration(fromIso, toIso) {
  if (!fromIso) return "";
  const from = new Date(fromIso);
  const to = toIso ? new Date(toIso) : new Date();
  const ms = to - from;
  if (ms < 0) return "";
  const mins = Math.round(ms / 60000);
  if (mins < 60) return `${mins} min`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} hr`;
  const days = Math.round(hrs / 24);
  if (days < 30) return `${days} day${days === 1 ? "" : "s"}`;
  const months = Math.round(days / 30);
  return `${months} month${months === 1 ? "" : "s"}`;
}

export default function StageTimeline({ history }) {
  if (!history || history.length === 0) return null;

  // history is appended in order; entries with no next have an open duration
  return (
    <div
      className="mt-4 rounded-2xl border border-slate-200 bg-white p-4"
      data-testid="stage-timeline"
    >
      <div className="mb-3 flex items-center gap-2">
        <Clock size={16} className="text-slate-600" />
        <h4 className="text-sm font-semibold uppercase tracking-wide text-slate-700">
          Stage Timeline
        </h4>
      </div>
      <ol className="relative ml-2 space-y-3 border-l border-slate-200 pl-5">
        {history.map((entry, idx) => {
          const meta = STAGE_META[entry.stage] || { label: entry.stage, dot: "bg-slate-400" };
          const nextEntry = history[idx + 1];
          const isCurrent = !nextEntry;
          const duration = formatDuration(entry.changedAt, nextEntry?.changedAt);
          return (
            <li key={`${entry.stage}-${entry.changedAt}-${idx}`} className="relative">
              <span
                className={`absolute -left-[27px] top-1 h-3 w-3 rounded-full ring-2 ring-white ${meta.dot}`}
                aria-hidden
              />
              <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                <p className="text-sm font-semibold text-slate-900">
                  {meta.label}
                  {isCurrent && (
                    <span className="ml-2 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-800">
                      Current
                    </span>
                  )}
                </p>
                {duration && (
                  <p className="text-xs text-slate-500">
                    {isCurrent ? `In stage for ${duration}` : duration}
                  </p>
                )}
              </div>
              <p className="mt-0.5 text-xs text-slate-500">{formatDateTime(entry.changedAt)}</p>
              {entry.changedBy && (
                <p className="mt-0.5 flex items-center gap-1 text-xs text-slate-500">
                  <User size={11} />
                  {entry.changedBy}
                </p>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}
